import { MessageStatus } from '@avanta/shared/constants/chat';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useMessagesStore } from '@avanta/shared/stores/chat/messages';
import type { OptimisticMessage, Conversation } from '@avanta/shared/types/chat';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PSYCHOLOGIST_ID } from 'constants/chat';
import { useChat } from 'hooks/useChat';
import { useUserStore } from 'stores/user';
import { getUserId } from 'utils/storage';
import {
  enqueueOnboardingMessages,
  getMessagesToProcess,
  type OnboardingState,
  processOnboardingMessage,
  resetConversationStateIfNeeded,
} from './utils';

const EMPTY_MESSAGES: OptimisticMessage[] = [];

export const useChatOnBoarding = (activeConversation?: Conversation | null): void => {
  const { t } = useTranslation(LOCIZE_NS.CHAT);

  const userId = activeConversation?.otherUser.id;
  const channelId = activeConversation?.channel.id;
  const patientId = getUserId();
  const conversationKey = `${channelId ?? ''}:${userId ?? ''}`;

  const { chat, isLoading: isLoadingChat } = useChat(userId);
  const userName = useUserStore(state => state.user?.firstName ?? state.user?.nickname ?? '');
  const onboardingRef = useRef<OnboardingState>({
    generation: 0,
    conversationKey: null,
    lastSeenTimetoken: null,
    failedTimetokens: new Set<string>(),
    queue: Promise.resolve(),
  });

  const availableMessage = t('OnBoarding.psychologistMessage', {
    ns: LOCIZE_NS.CHAT,
    name: userName,
  });
  const unavailableMessage = t('OnBoarding.psychologistMessageNotAvailable');

  const messages = useMessagesStore(
    state => (channelId ? state.messages.get(channelId) : undefined) ?? EMPTY_MESSAGES
  );

  useEffect(() => {
    const onboardingState = onboardingRef.current;
    resetConversationStateIfNeeded(conversationKey, onboardingState);

    if (
      !chat ||
      !channelId ||
      !patientId ||
      isLoadingChat ||
      userId !== PSYCHOLOGIST_ID ||
      chat.currentUser?.id !== userId
    ) {
      return;
    }

    const confirmedPatientMessages = messages.filter(
      message => message.status === MessageStatus.sent && message.userId === patientId
    );
    if (confirmedPatientMessages.length === 0) return;

    const { messagesToProcess, newestTimetoken } = getMessagesToProcess({
      confirmedPatientMessages,
      lastSeenTimetoken: onboardingState.lastSeenTimetoken,
      failedTimetokens: onboardingState.failedTimetokens,
    });
    onboardingState.lastSeenTimetoken = newestTimetoken;
    const generation = onboardingState.generation;

    enqueueOnboardingMessages({
      messagesToProcess,
      state: onboardingState,
      processMessage: patientMessage =>
        processOnboardingMessage({
          patientMessage,
          chat,
          channelId,
          patientId,
          availableMessage,
          unavailableMessage,
          isCurrentGeneration: () => onboardingState.generation === generation,
        }),
    });
  }, [
    availableMessage,
    channelId,
    chat,
    conversationKey,
    isLoadingChat,
    messages,
    patientId,
    unavailableMessage,
    userId,
  ]);
};
